import { DiscountType, ReleaseType } from '@ibk/shared-types';
import { formatCurrency } from '@ibk/shared-utils';
import { BeneficiaryStatus, BeneficiaryStatusInProduct, } from '../types/voucher-beneficiaries';
import { applyMask, DEFAULT_CURRENCY_VALUE } from './maskUtils';
function mapDiscountType(discountType) {
    if (discountType === 'FIXED_VALUE') {
        return DiscountType.TOTAL;
    }
    if (discountType === 'PERCENTAGE') {
        return DiscountType.PERCENTAGE;
    }
    return DiscountType.NO_DISCOUNT;
}
function mapDiscountValue(value, discountType) {
    if (value == null || discountType === DiscountType.NO_DISCOUNT) {
        return undefined;
    }
    return applyMask(String(Math.round(value * 100)), discountType);
}
function getBeneficiaryDiscount(beneficiary, discountType) {
    if (discountType === 'FIXED_VALUE') {
        return beneficiary.discountValue ?? 0;
    }
    if (discountType === 'PERCENTAGE') {
        return beneficiary.discountPercentage ?? 0;
    }
    return 0;
}
function getPreviousDaysQuantity(beneficiary, order) {
    if (beneficiary.daysQuantity != null && beneficiary.daysQuantity > 0) {
        return beneficiary.daysQuantity;
    }
    if (order.daysQuantity != null && order.daysQuantity > 0) {
        return order.daysQuantity;
    }
    return null;
}
function getDailyRate(beneficiary, order, product) {
    if (order.baseValue != null && order.baseValue > 0) {
        return order.baseValue;
    }
    const previousDays = getPreviousDaysQuantity(beneficiary, order);
    if (previousDays != null) {
        return beneficiary.amount / previousDays;
    }
    return product.amountDaily ?? beneficiary.amount;
}
function getBeneficiaryValue(beneficiary, order, product, releaseType, businessDays) {
    if (releaseType === ReleaseType.DAILY) {
        return getDailyRate(beneficiary, order, product) * businessDays;
    }
    if (order.baseValue == null) {
        return beneficiary.amount;
    }
    return order.baseValue;
}
function mapOrderBeneficiary(beneficiary, order, product, releaseType, businessDays) {
    const daysQuantity = releaseType === ReleaseType.DAILY ? businessDays : undefined;
    const value = getBeneficiaryValue(beneficiary, order, product, releaseType, businessDays);
    const discountValue = getBeneficiaryDiscount(beneficiary, order.discountType);
    return {
        selected: {
            beneficiaryId: beneficiary.beneficiaryId,
            value,
            daysQuantity,
            discountValue,
        },
        row: {
            id: beneficiary.beneficiaryId,
            jobRole: beneficiary.jobRole,
            document: beneficiary.document ?? '',
            firstName: beneficiary.firstName ?? '',
            lastName: beneficiary.lastName ?? '',
            employeeCode: beneficiary.employeeCode ?? '',
            deliveryLocationId: '',
            birthDate: new Date(0),
            phoneNumber: '',
            email: '',
            status: BeneficiaryStatus.WORKING,
            employeeId: '',
            isNew: false,
            productValue: value,
            statusInProduct: BeneficiaryStatusInProduct.ACTIVE,
            productDaysQuantity: daysQuantity ?? 0,
            productDiscountValue: discountValue,
            cardIssued: beneficiary.cardIssued,
            contractType: beneficiary.contractType ?? undefined,
        },
    };
}
export function mapEmissorOrderToFormData({ order, beneficiaries, product, competenceDate, creditDate, businessDays, }) {
    const releaseType = order.calculationType === 'DAILY' ? ReleaseType.DAILY : ReleaseType.MONTHLY;
    const activeBeneficiaries = beneficiaries.filter(beneficiary => beneficiary.isWorking);
    const mappedBeneficiaries = activeBeneficiaries.map(beneficiary => mapOrderBeneficiary(beneficiary, order, product, releaseType, businessDays));
    const discountType = mapDiscountType(order.discountType);
    const productDefaultValue = releaseType === ReleaseType.DAILY ? product.amountDaily : product.baseValue;
    const globalValue = order.baseValue ?? productDefaultValue;
    return {
        competenceDate,
        creditDate,
        voucherTypeId: order.product?.id ?? product.productId,
        voucherType: (product.productName ?? product.productId),
        rechargeIdentification: order.description ?? undefined,
        releaseType,
        globalValue: globalValue != null ? formatCurrency(globalValue) : DEFAULT_CURRENCY_VALUE,
        amountDaily: releaseType === ReleaseType.DAILY ? globalValue : undefined,
        businessDays: releaseType === ReleaseType.DAILY ? businessDays : undefined,
        discountType,
        payrollDeduction: discountType !== DiscountType.NO_DISCOUNT,
        discountValue: mapDiscountValue(order.discountValue, discountType),
        isIndividualDiscount: mappedBeneficiaries.some(({ selected }) => (selected.discountValue ?? 0) > 0),
        definedByBeneficiary: order.baseValue == null,
        beneficiaries: mappedBeneficiaries.map(({ row }) => row),
        selectedBeneficiaries: mappedBeneficiaries.length,
        selectedBeneficiariesList: mappedBeneficiaries.map(({ selected }) => selected),
        repeatRechargeExcludedBeneficiaryDiff: Math.max(0, beneficiaries.length - mappedBeneficiaries.length),
    };
}
